import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { t } from '@/@core/libs/i18n/utils'

export default function useCouponList() {
  // Use toast
  const toast = useToast()

  const refCouponListTable = ref(null)

  const msgCouponList = {
    errorfetchCoupon: t('Error fetching {module} list', t('Coupon')),
    brand_merchant: `${t('Brand')},${t('Merchant')}`,
    image: t('Image'),
    coupon: `${t('Coupon')} » ${t('Start Date')},${t('Stop Date')}`,
    value: `${t('Value')} » ${t('Create Date')},${t('Create By')}`,
    status: t('Status'),
    actions: t('Actions'),
  }

  // Table Handlers
  const tableColumns = [
    { key: 'brand_merchant', sortable: false, label: msgCouponList.brand_merchant },
    { key: 'image', sortable: false, label: msgCouponList.image },
    { key: 'coupon', sortable: true, label: msgCouponList.coupon },
    { key: 'value', sortable: true, label: msgCouponList.value },
    { key: 'status', sortable: true, label: msgCouponList.status },
    { key: 'actions', label: msgCouponList.actions },
  ]

  const perPage = ref(10)
  const totalCoupons = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [2, 5, 10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('code')
  const isSortDirDesc = ref(false)
  const brandFilter = ref(null)
  const merchantFilter = ref(null)
  const valueFilter = ref(null)
  const statusFilter = ref(null)

  const dataMeta = computed(() => {
    const localItemsCount = refCouponListTable.value ? refCouponListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalCoupons.value,
    }
  })

  const refetchData = () => {
    refCouponListTable.value.refresh()
  }

  watch([currentPage, perPage, searchQuery, brandFilter, merchantFilter, valueFilter, statusFilter], () => {
    refetchData()
  })

  const getCoupons = (ctx, callback) => {
    const params = {
      q: searchQuery.value,
      perPage: perPage.value,
      page: currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      brand: brandFilter.value,
      merchant: merchantFilter.value,
      value: valueFilter.value,
      status: statusFilter.value,
    }
    store
      .dispatch('coupon-store/getCoupons', params)
      .then(response => {
        const { coupons, total } = response.data
        callback(coupons)
        totalCoupons.value = total
        localStorage.setItem('filterCouponList', JSON.stringify(params))
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: msgCouponList.errorfetchCoupon,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        },
        {
          position: 'bottom-right',
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveCouponStatusVariant = status => {
    if (status === 'refunded') return 'info'
    if (status === 'available') return 'success'
    if (status === 'redeemed') return 'primary'
    return 'secondary'
  }

  const textFirstUpper = text => text.slice(0, 1).toUpperCase() + text.slice(1, text.length)

  return {
    getCoupons,
    tableColumns,
    perPage,
    currentPage,
    totalCoupons,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refCouponListTable,

    resolveCouponStatusVariant,
    refetchData,
    textFirstUpper,

    // Extra Filters
    brandFilter,
    merchantFilter,
    valueFilter,
    statusFilter,
  }
}
